<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';

import { dateFormat } from 'shared/helpers/timeHelper';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Table from 'dashboard/components/table/Table.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  loading: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();
const { getPlainText } = useMessageFormatter();

const EMPTY = '—';

function formatDate(ts) {
  if (ts == null || ts === '') return EMPTY;
  try {
    let unixSeconds;
    if (typeof ts === 'number') {
      unixSeconds = ts > 1e12 ? Math.floor(ts / 1000) : ts;
    } else {
      const parsed = Date.parse(ts);
      if (Number.isNaN(parsed)) return EMPTY;
      unixSeconds = Math.floor(parsed / 1000);
    }
    return dateFormat(unixSeconds, 'LLL d, yyyy');
  } catch {
    return EMPTY;
  }
}

function mediaList(item) {
  if (item.media_attachments?.length) return item.media_attachments;
  if (item.media_attachment) return [item.media_attachment];
  return [];
}

const rows = computed(() => props.items.map(item => ({ item })));

const columnHelper = createColumnHelper();

const columns = computed(() => [
  columnHelper.accessor(row => row.item.short_code ?? '', {
    id: 'short_code',
    header: t('CANNED_MGMT.LIST.TABLE_HEADER.NAME'),
    cell: ({ row }) =>
      h(
        'span',
        { class: 'font-semibold text-n-slate-12' },
        row.original.item.short_code || EMPTY
      ),
    size: 160,
  }),
  columnHelper.accessor(row => getPlainText(row.item.content) ?? '', {
    id: 'content',
    header: t('CANNED_MGMT.LIST.TABLE_HEADER.MESSAGE'),
    cell: ({ row }) =>
      h(
        'span',
        {
          class: 'line-clamp-2 max-w-md break-words text-sm text-n-slate-12',
        },
        getPlainText(row.original.item.content) || EMPTY
      ),
    size: 280,
  }),
  columnHelper.display({
    id: 'files',
    header: t('CANNED_MGMT.LIST.TABLE_HEADER.FILES'),
    cell: ({ row }) => {
      const files = mediaList(row.original.item);
      if (!files.length) {
        return h('span', { class: 'text-sm text-n-slate-12' }, EMPTY);
      }
      return h(
        'div',
        { class: 'flex min-w-0 flex-col gap-0.5' },
        files.map(f =>
          h(
            'a',
            {
              class:
                'truncate text-sm text-n-blue-11 hover:underline max-w-[14rem]',
              href: f.data_url,
              target: '_blank',
              rel: 'noopener noreferrer',
            },
            f.filename || EMPTY
          )
        )
      );
    },
    size: 180,
  }),
  columnHelper.accessor(row => row.item.id, {
    id: 'id',
    header: t('CANNED_MGMT.LIST.TABLE_HEADER.FIELD_ID'),
    cell: ({ row }) =>
      h(
        'span',
        { class: 'font-mono text-sm text-n-slate-12' },
        String(row.original.item.id ?? EMPTY)
      ),
    size: 88,
  }),
  columnHelper.accessor(row => row.item.created_at, {
    id: 'created_at',
    header: t('CANNED_MGMT.LIST.TABLE_HEADER.DATE_ADDED'),
    cell: ({ row }) =>
      h(
        'span',
        { class: 'whitespace-nowrap text-sm text-n-slate-12' },
        formatDate(row.original.item.created_at)
      ),
    size: 140,
  }),
  columnHelper.display({
    id: 'actions',
    header: () =>
      h(
        'span',
        { class: 'text-base font-bold text-n-slate-12 normal-case' },
        t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS')
      ),
    enableSorting: false,
    cell: ({ row }) => {
      const item = row.original.item;
      return h(
        'div',
        { class: 'flex flex-row items-center justify-end gap-2' },
        [
          h(Button, {
            slate: true,
            sm: true,
            icon: 'i-woot-edit-pen',
            title: t('CANNED_MGMT.EDIT.BUTTON_TEXT'),
            isLoading: props.loading[item.id],
            onClick: () => emit('edit', item),
          }),
          h(Button, {
            slate: true,
            sm: true,
            icon: 'i-woot-bin',
            class: 'hover:enabled:bg-n-ruby-2 hover:enabled:text-n-ruby-11',
            title: t('CANNED_MGMT.DELETE.BUTTON_TEXT'),
            isLoading: props.loading[item.id],
            onClick: () => emit('delete', item),
          }),
        ]
      );
    },
    size: 96,
  }),
]);

const table = useVueTable({
  get data() {
    return rows.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: true,
  getRowId: row => String(row.item.id),
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
});
</script>

<template>
  <Table :table="table" type="compact" panel="soft" />
</template>
